/**
 * Telemetry — oscilloscope-style data-viz rendered into the desktop rails.
 *
 * Two canvases, one per rail. Left traces a smooth "signal" sine derived
 * from the inverse of the void index (healthy humanity = steady waveform).
 * Right traces jittery "void pressure" noise with occasional spikes when
 * the Alien Void advances.
 *
 * Uses the 2D canvas API for portability. GPU is reserved for the Phaser
 * scene — these rails are <200px of data-viz and don't justify WebGPU.
 */

import { world } from '@/net/PlayroomBridge';

const HISTORY = 360;

type Scope = {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  buf: number[];
  color: string;
  jitter: boolean;
};

export function mountTelemetryGraphs(): void {
  const left = setupScope('telemetry-left', '#ffffff', false);
  const right = setupScope('telemetry-right', '#ff2040', true);
  if (!left && !right) return;

  let t = 0;
  const loop = (): void => {
    t += 1;
    if (left) pushLeft(left, t);
    if (right) pushRight(right, t);
    [left, right].forEach((s) => s && render(s));
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  // Re-sync canvas size on layout changes.
  window.addEventListener('resize', () => {
    [left, right].forEach((s) => s && sizeCanvas(s.canvas));
  });
}

function setupScope(id: string, color: string, jitter: boolean): Scope | null {
  const canvas = document.getElementById(id) as HTMLCanvasElement | null;
  if (!canvas) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  sizeCanvas(canvas);
  return {
    canvas,
    ctx,
    buf: new Array<number>(HISTORY).fill(0.5),
    color,
    jitter,
  };
}

function sizeCanvas(canvas: HTMLCanvasElement): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.max(1, Math.floor(rect.width * dpr));
  canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function pushLeft(s: Scope, t: number): void {
  const v = world.getVoidIndex();
  const base = 0.5 + (1 - v) * 0.35 * Math.sin(t * 0.07);
  const wobble = 0.02 * Math.sin(t * 0.31);
  s.buf.push(clamp01(base + wobble));
  s.buf.shift();
}

function pushRight(s: Scope, t: number): void {
  const v = world.getVoidIndex();
  const spike = Math.random() < 0.02 + v * 0.05 ? (Math.random() - 0.5) * 0.9 : 0;
  const noise = (Math.random() - 0.5) * (0.15 + v * 0.5);
  const trend = 0.5 + v * 0.3 * Math.sin(t * 0.03);
  s.buf.push(clamp01(trend + noise + spike));
  s.buf.shift();
}

function render(s: Scope): void {
  const { ctx, buf, color, canvas } = s;
  const w = canvas.clientWidth || canvas.width;
  const h = canvas.clientHeight || canvas.height;
  ctx.clearRect(0, 0, w, h);

  // Graticule.
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 1; i < 4; i++) {
    const y = (h / 4) * i;
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
  }
  for (let i = 1; i < 6; i++) {
    const x = (w / 6) * i;
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
  }
  ctx.stroke();

  // Trace.
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.shadowBlur = 6;
  ctx.shadowColor = color;
  ctx.beginPath();
  for (let i = 0; i < buf.length; i++) {
    const x = (i / (buf.length - 1)) * w;
    const y = h - buf[i] * h;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.shadowBlur = 0;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}
